import { useState, useCallback } from "react";
import axios from "axios";
import { Moment } from "moment";
import cSharpApi from "../../../../../../services/api/CSharpApi";

export type LineTemperatureHistoricalData = {
  time: Moment;
  line: number;
  temperature: number;
  lineDisplayUnit: string;
  temperatureDisplayUnit: string;
};

export type LineTemperatureChartData = {
  historicalData: LineTemperatureHistoricalData[];
  hasData: boolean;
  errorMessage: string;
};

type LineTemperatureChartState = {
  data: LineTemperatureChartData;
  isLoading: boolean;
  hasError: boolean;
  errorMessage?: string;
  message?: string;
};

type FetchParams = {
  duration: number;
  resolution: number;
  customEndDate?: number;
  hoursToAdd: number;

  lineName: string;
  lineFieldKey: string;
  temperatureName: string;
  temperatureFieldKey: string;

  lineDisplayName?: string;
  lineFieldName?: string;
  temperatureDisplayName?: string;
  temperatureFieldName?: string;
  linePortNumber: number;
  temperaturePortNumber: number;
  orgId?: string;
};

const INITIAL_STATE = {
  data: {
    historicalData: [],
    hasData: false,
    errorMessage: "",
  },
  isLoading: false,
  hasError: false,
  errorMessage: "",
  message: "",
};

const useHistoricalLineAndTemperature = (deviceId: string) => {
  const [fetchedData, setFetchedData] = useState<LineTemperatureChartState>(INITIAL_STATE);

  const cancelTokenSource = axios.CancelToken.source();

  const fetchData = useCallback(
    async (params: FetchParams) => {
      setFetchedData({
        ...INITIAL_STATE,
        isLoading: true,
      });

      try {
        const response = await cSharpApi.post(
          `charts/get-historical-line-and-temperature/${deviceId}`,
          JSON.stringify(params),
          {
            cancelToken: cancelTokenSource.token,
          },
        );

        const data = await response.data.data;

        if (data) {
          setFetchedData({
            data,
            isLoading: false,
            hasError: false,
            errorMessage: "",
            message: response.data.message,
          });
        }
      } catch (error) {
        let errorMessage = "fetching data aborted";

        if (axios.isCancel(error)) {
          console.log("fetching data aborted");
        } else {
          errorMessage = error instanceof Error ? error.message : "Unknown error";
          console.log("An error occurred", error);
        }

        setFetchedData({
          ...INITIAL_STATE,
          errorMessage,
        });
      }
      return () => cancelTokenSource.cancel();
    },
    [deviceId],
  );

  const { data, isLoading, hasError, errorMessage, message } = fetchedData;

  return { fetchData, data, isLoading, hasError, errorMessage, message };
};

export default useHistoricalLineAndTemperature;
